import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Waypoints,
  ListChecks,
  Newspaper,
  CalendarDays,
  Ban,
  FileText,
  Scale,
  Server,
  Calculator,
  type LucideIcon,
} from 'lucide-react';
import { Badge, type BadgeTone } from '../../ui';
import { KIND_ORDER, kindLabel } from '../../features/intelligence/contextRetriever';
import type { EntityKind, EntityRef } from '../../features/intelligence/types';

const KIND_ICON: Record<EntityKind, LucideIcon> = {
  goal: Target,
  workstream: Waypoints,
  story: ListChecks,
  change: Newspaper,
  dailyUpdate: CalendarDays,
  blocker: Ban,
  document: FileText,
  decision: Scale,
  service: Server,
  estimate: Calculator,
};

const KIND_TONE: Record<EntityKind, BadgeTone> = {
  goal: 'accent',
  workstream: 'info',
  story: 'neutral',
  change: 'neutral',
  dailyUpdate: 'neutral',
  blocker: 'danger',
  document: 'info',
  decision: 'warning',
  service: 'neutral',
  estimate: 'neutral',
};

export interface EntityRefLinksProps {
  refs: EntityRef[];
  /** Optional heading shown above the chips. */
  heading?: string;
  /** Rendered when there are no refs (omitted entirely when undefined). */
  emptyLabel?: string;
  /** Group chips under per-kind subheadings (default false = one flat row). */
  grouped?: boolean;
}

/**
 * Renders a set of {@link EntityRef}s as clickable source chips that deep-link
 * back to the exact entity (Goal / Workstream / Story / Document / Decision /
 * Service / …) — the "sources" surface shared by Ask Vector, the Digest, and
 * Catch Me Up. Chips with a route navigate; routeless kinds render as static
 * chips. Purely presentational: no data metrics, just provenance + navigation.
 */
export function EntityRefLinks({
  refs,
  heading,
  emptyLabel,
  grouped = false,
}: EntityRefLinksProps) {
  const navigate = useNavigate();

  const groups = useMemo(() => {
    if (!grouped) return null;
    const byKind = new Map<EntityKind, EntityRef[]>();
    for (const r of refs) {
      const list = byKind.get(r.kind) ?? [];
      list.push(r);
      byKind.set(r.kind, list);
    }
    return KIND_ORDER.filter((k) => byKind.has(k)).map((k) => ({
      kind: k,
      items: byKind.get(k) ?? [],
    }));
  }, [refs, grouped]);

  if (refs.length === 0) {
    return emptyLabel ? (
      <p className="text-xs text-zinc-500">{emptyLabel}</p>
    ) : null;
  }

  const chip = (r: EntityRef) => {
    const Icon = KIND_ICON[r.kind];
    const tone = KIND_TONE[r.kind];
    const clickable = !!r.route;
    return (
      <button
        key={`${r.kind}:${r.id}`}
        type="button"
        disabled={!clickable}
        onClick={clickable ? () => navigate(r.route as string) : undefined}
        title={`${kindLabel(r.kind)} · ${r.title}`}
        className={clickable ? 'inline-flex text-left' : 'inline-flex cursor-default text-left'}
      >
        <Badge tone={tone}>
          <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="max-w-[16rem] truncate">{r.title}</span>
        </Badge>
      </button>
    );
  };

  return (
    <div className="space-y-2">
      {heading && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          {heading}
        </p>
      )}
      {grouped && groups ? (
        <div className="space-y-2.5">
          {groups.map((g) => (
            <div key={g.kind}>
              <p className="mb-1 text-[11px] text-zinc-500">
                {kindLabel(g.kind)}s ({g.items.length})
              </p>
              <div className="flex flex-wrap gap-1.5">{g.items.map(chip)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">{refs.map(chip)}</div>
      )}
    </div>
  );
}

export default EntityRefLinks;
